import { Check, Eye } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { DocumentTemplate } from '@/services/types/letters-types';

interface TemplateSelectionCardProps {
  template: DocumentTemplate;
  isSelected: boolean;
  onSelect: (template: DocumentTemplate) => void;
  onPreview: (template: DocumentTemplate) => void;
}

export const TemplateSelectionCard: React.FC<TemplateSelectionCardProps> =
  React.memo(({ template, isSelected, onSelect, onPreview }) => {
    const handleCardClick = React.useCallback(() => {
      onSelect(template);
    }, [template, onSelect]);

    const handlePreviewClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onPreview(template);
      },
      [template, onPreview],
    );

    return (
      <Card
        className={cn(
          'cursor-pointer transition-all duration-200 bg-white border shadow-sm hover:shadow-md',
          isSelected
            ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
            : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300',
        )}
        onClick={handleCardClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-gray-900 text-base">
                  {template.name}
                </h3>
                {isSelected && (
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-5 h-5 bg-blue-600 rounded-full">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={cn(
                    'text-xs font-medium',
                    template.requires_signatures
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-700',
                  )}
                >
                  {template.requires_signatures
                    ? 'Requires signatures'
                    : 'No signatures required'}
                </Badge>
              </div>
            </div>
            <button
              type="button"
              onClick={handlePreviewClick}
              className="flex-shrink-0 ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-md transition-colors"
              title="Preview template"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    );
  });

TemplateSelectionCard.displayName = 'TemplateSelectionCard';
